import { FC } from 'react';

import { useOptions } from '@core/contexts/OptionsContext';
import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Flex } from '@shared/components/flex';
import { NumberField } from '@shared/components/number-field';
import { MultiSelect } from '@shared/components/select-new/multi-select';
import { SingleSelect } from '@shared/components/select-new/single-select';
import { TextField } from '@shared/components/text-field';
import { ButtonSize } from '@shared/types/common/button';
import { InputSize } from '@shared/types/common/input';

import { styles } from './InvestorProfile.styles';

export interface InvestorProfileFormData {
  investor_aum: string;
  investor_aum_currency: string;
  investor_current_holdings: string;
  investor_other_details: string;
  investor_ranking: string[];
  investor_region: string[];
  investor_type: string[];
  nationality: string;
}

export interface InvestorProfileProps extends WithStyles<typeof styles> {
  readonly?: boolean;
  values: InvestorProfileFormData;
  withLabel?: boolean;
  onChange: ChangeCallback<InvestorProfileFormData>;
}

const InvestorProfileComponent: FC<InvestorProfileProps> = ({
  classes,
  readonly = false,
  values,
  withLabel = true,
  onChange,
}) => {
  const {
    countryOptions,
    investmentCurrencyOptions,
    investorRankingOptions,
    investorRegionOptions,
    investorTypeOptions,
  } = useOptions();

  return (
    <Flex classes={{ root: classes.root }}>
      {withLabel && <div className={classes.heading}>{t('investor_profile')}</div>}

      <Flex
        autoWidth={false}
        classes={{ root: classes.row }}
        direction="row"
        wrap="nowrap"
        justifyContent="space-between"
      >
        <div className={classes.field}>
          <div className={classes.label}>{t('nationality')}</div>
          <SingleSelect
            classes={{ root: classes.selectRoot }}
            disabled={readonly}
            options={countryOptions}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={values.nationality}
            onChange={(value) => onChange('nationality')(value as string)}
          />
        </div>

        <div className={classes.field}>
          <div className={classes.label}>{t('investor_type')}</div>
          <MultiSelect
            classes={{ root: classes.selectRoot }}
            disabled={readonly}
            options={investorTypeOptions}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={values.investor_type ?? []}
            onChange={(value) => onChange('investor_type')(value as string[])}
          />
        </div>
      </Flex>

      <Flex
        autoWidth={false}
        classes={{ root: classes.row }}
        direction="row"
        wrap="nowrap"
        justifyContent="space-between"
      >
        <div className={classes.field}>
          <div className={classes.label}>{t('region')}</div>
          <MultiSelect
            classes={{ root: classes.selectRoot }}
            disabled={readonly}
            options={investorRegionOptions}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={values.investor_region ?? []}
            onChange={(value) => onChange('investor_region')(value as string[])}
          />
        </div>

        <div className={classes.field}>
          <div className={classes.label}>{t('ranking')}</div>
          <MultiSelect
            classes={{ root: classes.selectRoot }}
            disabled={readonly}
            options={investorRankingOptions}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={values.investor_ranking ?? []}
            onChange={(value) => onChange('investor_ranking')(value as string[])}
          />
        </div>
      </Flex>

      <Flex
        autoWidth={false}
        classes={{ root: classes.row }}
        direction="row"
        wrap="nowrap"
        justifyContent="space-between"
      >
        <div className={classes.field}>
          <div className={classes.label}>{t('aum')}</div>
          <NumberField
            disabled={readonly}
            fullWidth
            placeholder={t('...')}
            size={InputSize.medium}
            value={values.investor_aum ?? ''}
            onChange={(value) => onChange('investor_aum')(value)}
          />
        </div>

        <div className={classes.field}>
          <div className={classes.label}>{t('aum_currency')}</div>
          <SingleSelect
            classes={{ root: classes.selectRoot }}
            disabled={readonly}
            options={investmentCurrencyOptions}
            placeholder={t('xtextx_ellipsis', { text: t('please_select') })}
            size={ButtonSize.medium}
            value={values.investor_aum_currency}
            onChange={(value) => onChange('investor_aum_currency')(value as string)}
          />
        </div>
      </Flex>

      <Flex
        autoWidth={false}
        classes={{ root: classes.row }}
        direction="row"
        wrap="nowrap"
        justifyContent="space-between"
      >
        <div className={classes.fieldFulWidth}>
          <div className={classes.label}>{t('current_holdings')}</div>
          <TextField
            disabled={readonly}
            fullWidth
            placeholder={t('...')}
            size={InputSize.medium}
            value={values.investor_current_holdings ?? ''}
            onChange={(e) => onChange('investor_current_holdings')(e.target.value)}
          />
        </div>
      </Flex>

      <Flex
        autoWidth={false}
        classes={{ root: classes.row }}
        direction="row"
        wrap="nowrap"
        justifyContent="space-between"
      >
        <div className={classes.fieldFulWidth}>
          <div className={classes.label}>{t('other_investor_details')}</div>
          <TextField
            disabled={readonly}
            fullWidth
            maxRows={4}
            minRows={4}
            multiline={true}
            placeholder={t('...')}
            size={InputSize.medium}
            value={values.investor_other_details ?? ''}
            onChange={(e) => onChange('investor_other_details')(e.target.value)}
          />
        </div>
      </Flex>
    </Flex>
  );
};

export const InvestorProfile = withStyles(styles)(InvestorProfileComponent);
